/**
 * @file     copy.ts
 * @purpose  Typed access to the `rentalsListing` English namespace. The
 *           English source lives once in resources/en/rentalsListing.ts and
 *           rides along as the defaultValue, so a key cannot drift from its
 *           copy. `rentalsText` serves Server Components (the server i18n
 *           instance); `useRentalsText` serves client islands.
 */
import '@/core/i18n/deferred/rentalsListing';
import { rentalsListing } from '@/core/i18n/resources/en/rentalsListing';

type Leaves<T, P extends string = ''> = {
  [K in keyof T & string]: T[K] extends string ? `${P}${K}` : Leaves<T[K], `${P}${K}.`>;
}[keyof T & string];

export type RentalsKey = Leaves<typeof rentalsListing>;
export type RentalsVars = Record<string, string | number>;
type Translate = (key: string, options: Record<string, unknown>) => string;

export function rentalsDefault(key: RentalsKey): string {
  let node: unknown = rentalsListing;
  for (const part of key.split('.')) node = (node as Record<string, unknown>)[part];
  return typeof node === 'string' ? node : key;
}

/** Fill `{{name}}` slots without i18next, for the plain-English default path. */
function fillDefault(template: string, vars: RentalsVars = {}): string {
  return template.replace(/\{\{(\w+)\}\}/g, (m, name: string) => (name in vars ? String(vars[name]) : m));
}

export function translateRentals(t: Translate | null, key: RentalsKey, vars?: RentalsVars): string {
  const defaultValue = rentalsDefault(key);
  if (!t) return fillDefault(defaultValue, vars);
  const out = t(`rentalsListing.${key}`, { defaultValue, ...vars });
  if (out === `rentalsListing.${key}`) return fillDefault(defaultValue, vars);
  // An i18n instance that is not ready yet returns the default without interpolating it.
  return out.includes('{{') ? fillDefault(out, vars) : out;
}
